import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, RotateCcw, Plus, Trash2, AlertCircle, CheckCircle, Loader, Mail } from 'lucide-react';
import {
  useGetEmailTemplateQuery,
  useCreateOrUpdateEmailTemplateMutation,
  useResetEmailTemplateMutation,
  EmailFormat,
} from '../../store/emailTemplateApi';
import { useGetVendorMeQuery } from '../../store/vendorApi';
import {
  EDITABLE_TEMPLATE_TYPES,
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_CONFIGS,
  type EditableTemplateType,
} from './templateSections';
import { assembleEmailHtml, replaceVariablesWithSamples, type SectionValues } from './htmlAssembler';
import { parseSectionsFromHtml, getDefaultSections } from './sectionParser';
import VariableChipBar from './VariableChipBar';
import ParagraphRichEditor from './ParagraphRichEditor';

const StructuredEmailEditor: React.FC = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'settings' });
  const [selectedType, setSelectedType] = useState<EditableTemplateType>(EDITABLE_TEMPLATE_TYPES[0]);
  const [subject, setSubject] = useState('');
  const [sections, setSections] = useState<SectionValues>(() => getDefaultSections(EDITABLE_TEMPLATE_TYPES[0]));
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Track which field is focused for variable insertion
  const focusedFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const config = TEMPLATE_CONFIGS[selectedType];

  // API hooks
  const { data: template, isLoading: isLoadingTemplate } = useGetEmailTemplateQuery(selectedType);
  const [createOrUpdate, { isLoading: isSaving }] = useCreateOrUpdateEmailTemplateMutation();
  const [resetTemplate, { isLoading: isResetting }] = useResetEmailTemplateMutation();

  // DEV-179 — preview vendor-owned variables with the vendor's real values
  // instead of generic samples ("Acme Inc", "Jane Smith", ...). Recipient-specific
  // variables (e.g. {{firstName}}) have no real value at edit time and keep their samples.
  const { data: vendor } = useGetVendorMeQuery();
  const realVendorName = vendor ? [vendor.firstName, vendor.lastName].filter(Boolean).join(' ').trim() : '';
  const realCompanyName = vendor?.companyName?.trim() ?? '';
  const realProductNames = (vendor?.pageInfo?.products ?? []).map(p => p.name).filter(Boolean).join(', ');

  const applyRealVendorValues = (html: string): string => {
    if (!vendor) return html;
    let out = html;
    // Only override when we actually have a value, so missing data still falls
    // back to the sample — except productNames, where empty is the real state
    // (matches DEV-178: no products => the tag resolves to empty).
    if (realCompanyName) out = out.replace(/\{\{companyName\}\}/g, realCompanyName);
    if (realVendorName) out = out.replace(/\{\{vendorName\}\}/g, realVendorName);
    out = out.replace(/\{\{productNames\}\}/g, realProductNames);
    return out;
  };

  // Load template data when template changes
  useEffect(() => {
    if (template) {
      setSubject(template.subject);
      const parsed = parseSectionsFromHtml(template.body, selectedType);
      setSections(parsed);
    } else if (!isLoadingTemplate) {
      // No saved template, use defaults
      const defaults = getDefaultSections(selectedType);
      setSubject(config.defaultSubject);
      setSections(defaults);
    }
  }, [template, selectedType, isLoadingTemplate, config.defaultSubject]);

  // Handle template type change
  const handleTypeChange = (type: EditableTemplateType) => {
    setSelectedType(type);
    setSuccessMessage('');
    setErrorMessage('');
  };

  // Section update helpers
  const updateGreeting = (value: string) => {
    setSections(prev => ({ ...prev, greeting: value }));
  };

  const updateParagraph = (index: number, value: string) => {
    setSections(prev => {
      const updated = [...prev.bodyParagraphs];
      updated[index] = value;
      return { ...prev, bodyParagraphs: updated };
    });
  };

  const addParagraph = () => {
    if (sections.bodyParagraphs.length < config.maxParagraphs) {
      setSections(prev => ({
        ...prev,
        bodyParagraphs: [...prev.bodyParagraphs, ''],
      }));
    }
  };

  const removeParagraph = (index: number) => {
    if (sections.bodyParagraphs.length > 1) {
      setSections(prev => ({
        ...prev,
        bodyParagraphs: prev.bodyParagraphs.filter((_, i) => i !== index),
      }));
    }
  };

  const updateCtaLabel = (value: string) => {
    setSections(prev => ({ ...prev, ctaLabel: value }));
  };

  const updateClosingText = (value: string) => {
    setSections(prev => ({ ...prev, closingText: value }));
  };

  // Insert variable at cursor position in focused field
  const handleInsertVariable = useCallback((token: string) => {
    const field = focusedFieldRef.current;
    if (!field) return;

    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? start;
    const newValue = field.value.slice(0, start) + token + field.value.slice(end);

    // Determine which field this belongs to and update state
    const fieldName = field.dataset.fieldName;
    const fieldIndex = field.dataset.fieldIndex;

    if (fieldName === 'subject') {
      setSubject(newValue);
    } else if (fieldName === 'greeting') {
      updateGreeting(newValue);
    } else if (fieldName === 'paragraph' && fieldIndex !== undefined) {
      updateParagraph(parseInt(fieldIndex), newValue);
    } else if (fieldName === 'ctaLabel') {
      updateCtaLabel(newValue);
    } else if (fieldName === 'closingText') {
      updateClosingText(newValue);
    }

    // Restore focus and cursor position after React re-render
    requestAnimationFrame(() => {
      field.focus();
      const newCursor = start + token.length;
      field.setSelectionRange(newCursor, newCursor);
    });
  }, []);

  // Debounced preview HTML
  const [previewHtml, setPreviewHtml] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const assembled = assembleEmailHtml(selectedType, sections);
      const withReal = applyRealVendorValues(assembled);
      const withSamples = replaceVariablesWithSamples(withReal, selectedType);
      setPreviewHtml(withSamples);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [sections, selectedType, realVendorName, realCompanyName, realProductNames]);

  // Save handler
  const handleSave = async () => {
    setErrorMessage('');
    setSuccessMessage('');

    if (!subject.trim()) {
      setErrorMessage(t('emailEditor.subjectEmpty'));
      return;
    }

    try {
      const fullHtml = assembleEmailHtml(selectedType, sections);
      await createOrUpdate({
        type: selectedType,
        subject,
        body: fullHtml,
        format: EmailFormat.HTML,
      }).unwrap();
      setSuccessMessage(t('emailEditor.savedSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setErrorMessage(err?.data?.error || err?.data?.message || t('emailEditor.saveFailed'));
    }
  };

  // Reset handler
  const handleReset = async () => {
    if (!window.confirm(t('emailEditor.resetConfirm'))) return;

    setErrorMessage('');
    setSuccessMessage('');

    try {
      const resetData = await resetTemplate(selectedType).unwrap();
      setSubject(resetData.subject);
      const parsed = parseSectionsFromHtml(resetData.body, selectedType);
      setSections(parsed);
      setSuccessMessage(t('emailEditor.resetSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setErrorMessage(err?.data?.error || err?.data?.message || t('emailEditor.resetFailed'));
    }
  };

  // Common field props for tracking focus
  const fieldProps = (fieldName: string, fieldIndex?: number) => ({
    'data-field-name': fieldName,
    'data-field-index': fieldIndex?.toString(),
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      focusedFieldRef.current = e.target;
    },
  });

  // Preview subject with samples
  const previewSubject = useMemo(() => {
    let result = applyRealVendorValues(subject);
    for (const v of config.variables) {
      result = result.replace(new RegExp(`\\{\\{${v.name}\\}\\}`, 'g'), v.sampleValue);
    }
    return result;
  }, [subject, config.variables, realVendorName, realCompanyName, realProductNames]);

  return (
    <div className="flex flex-col lg:flex-row gap-6 min-h-[600px]">
      {/* Left Panel - Form */}
      <div className="lg:w-1/2 space-y-5">
        {/* Template Type Selector */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('emailEditor.templateType')}</label>
          <select
            value={selectedType}
            onChange={(e) => handleTypeChange(e.target.value as EditableTemplateType)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
          >
            {EDITABLE_TEMPLATE_TYPES.map((type) => (
              <option key={type} value={type}>
                {TEMPLATE_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">{config.description}</p>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {errorMessage}
          </div>
        )}

        {isLoadingTemplate ? (
          <div className="flex items-center justify-center py-16">
            <Loader className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
            {/* Variable Chips */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                {t('emailEditor.insertVariables')}
              </label>
              <VariableChipBar
                variables={config.variables}
                onInsert={handleInsertVariable}
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('emailEditor.subjectLine')}</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder={t('emailEditor.subjectPlaceholder')}
                {...fieldProps('subject')}
              />
            </div>

            {/* Greeting */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('emailEditor.greeting')}</label>
              <input
                type="text"
                value={sections.greeting}
                onChange={(e) => updateGreeting(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder={t('emailEditor.greetingPlaceholder')}
                {...fieldProps('greeting')}
              />
            </div>

            {/* Body Paragraphs */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  {t('emailEditor.bodyParagraphs')}
                </label>
                {sections.bodyParagraphs.length < config.maxParagraphs && (
                  <button
                    type="button"
                    onClick={addParagraph}
                    className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t('emailEditor.addParagraph')}
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {sections.bodyParagraphs.map((paragraph, index) => (
                  <div key={`${selectedType}-${index}`} className="relative">
                    <ParagraphRichEditor
                      value={paragraph}
                      onChange={(html) => updateParagraph(index, html)}
                      placeholder={t('emailEditor.paragraphPlaceholder', { number: index + 1 })}
                      variables={config.variables}
                    />
                    {sections.bodyParagraphs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeParagraph(index)}
                        className="absolute top-1.5 right-1.5 p-1 text-gray-400 hover:text-red-500 transition-colors bg-white/80 rounded"
                        title={t('emailEditor.removeParagraph')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button Label */}
            {config.ctaLinkVariable && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Button Label
                </label>
                <input
                  type="text"
                  value={sections.ctaLabel}
                  onChange={(e) => updateCtaLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="e.g. Set Your Password"
                  {...fieldProps('ctaLabel')}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Button links to <code className="bg-gray-100 px-1 rounded">{`{{${config.ctaLinkVariable}}}`}</code> (automatic)
                </p>
              </div>
            )}

            {/* Closing Text */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Closing / Warning Text
              </label>
              <textarea
                value={sections.closingText}
                onChange={(e) => updateClosingText(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-y"
                placeholder="Closing message..."
                {...fieldProps('closingText')}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isSaving ? 'Saving...' : 'Save Template'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isResetting}
                className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
              >
                {isResetting ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Reset to Default
              </button>
            </div>
          </>
        )}
      </div>

      {/* Right Panel - Live Preview */}
      <div className="lg:w-1/2 flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Live Preview</span>
          <span className="text-xs text-gray-400">(sample data)</span>
        </div>

        {/* Subject preview */}
        <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <span className="text-xs font-medium text-gray-500">Subject: </span>
          <span className="text-sm text-gray-900">{previewSubject}</span>
        </div>

        {/* Email body preview */}
        <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden bg-white">
          <iframe
            srcDoc={previewHtml}
            title="Email Preview"
            className="w-full h-full min-h-[500px]"
            sandbox="allow-same-origin"
            style={{ border: 'none' }}
          />
        </div>
      </div>
    </div>
  );
};

export default StructuredEmailEditor;
